// src/features/feedback/hooks/useFeedbackVote.js
import { useState, useCallback } from 'react';
import { feedbackVoteService } from '../services/feedbackVoteService';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import i18n from '../../../i18n';

export function useFeedbackVote() {
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState(null);
  const { showSuccess, showError } = useSnackbar();

  /**
   * Toggle vote on feedback
   */
  const toggleVote = useCallback(async (feedbackId, currentlyVoted) => {
    setVoting(true);
    setError(null);

    try {
      let response;
      if (currentlyVoted) {
        response = await feedbackVoteService.removeVote(feedbackId);
        showSuccess(i18n.t('feedback:messages.vote_removed'));
      } else {
        response = await feedbackVoteService.voteOnFeedback(feedbackId);
        showSuccess(i18n.t('feedback:messages.vote_added'));
      }
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to update vote';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setVoting(false);
    }
  }, [showSuccess, showError]);

  /**
   * Vote on feedback
   */
  const voteOnFeedback = useCallback(async (feedbackId) => {
    setVoting(true);
    setError(null);

    try {
      const response = await feedbackVoteService.voteOnFeedback(feedbackId);
      showSuccess(i18n.t('feedback:messages.vote_added'));
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to add vote';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setVoting(false);
    }
  }, [showSuccess, showError]);

  /**
   * Remove vote from feedback
   */
  const removeVote = useCallback(async (feedbackId) => {
    setVoting(true);
    setError(null);

    try {
      const response = await feedbackVoteService.removeVote(feedbackId);
      showSuccess(i18n.t('feedback:messages.vote_removed'));
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to remove vote';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setVoting(false);
    }
  }, [showSuccess, showError]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    voting,
    error,
    toggleVote,
    voteOnFeedback,
    removeVote,
    clearError,
  };
}

/**
 * Hook for vote statistics (admin only)
 */
export function useFeedbackVoteStatistics() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showError } = useSnackbar();

  /**
   * Fetch vote statistics
   */
  const fetchVoteStatistics = useCallback(async (feedbackId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await feedbackVoteService.getVoteStatistics(feedbackId);
      setStatistics(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch vote statistics';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * Clear statistics
   */
  const clearStatistics = useCallback(() => {
    setStatistics(null);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    statistics,
    loading,
    error,
    fetchVoteStatistics,
    clearStatistics,
    clearError,
  };
}